import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import {
  ShieldCheck,
  Smartphone,
  KeyRound,
  Mail,
  Copy,
  Download,
  AlertTriangle,
} from "lucide-react";

/* -------------------- Change password -------------------- */
export function ChangePasswordDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (form.new_password.length < 6) return toast.error("La nuova password deve essere di almeno 6 caratteri");
    if (form.new_password !== form.confirm) return toast.error("Le due nuove password non coincidono");
    setLoading(true);
    try {
      await api.post("/auth/me/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password aggiornata");
      onOpenChange(false);
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err?.friendlyMessage || "Errore");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle>Cambia password</DialogTitle>
          <DialogDescription>
            Per motivi di sicurezza inserisci la password attuale prima di sceglierne una nuova.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Password attuale</Label>
            <Input
              type="password"
              value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })}
              data-testid="cp-current"
            />
          </div>
          <div>
            <Label>Nuova password</Label>
            <Input
              type="password"
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              data-testid="cp-new"
            />
          </div>
          <div>
            <Label>Conferma nuova password</Label>
            <Input
              type="password"
              value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              data-testid="cp-confirm"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={submit} disabled={loading} data-testid="cp-save">
            {loading ? "Salvataggio…" : "Aggiorna password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------- Change email -------------------- */
export function ChangeEmailDialog({ open, onOpenChange, onDone }) {
  const [form, setForm] = useState({ new_email: "", current_password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/auth/me/change-email", form);
      toast.success("Email aggiornata");
      onOpenChange(false);
      setForm({ new_email: "", current_password: "" });
      onDone?.();
    } catch (err) {
      toast.error(err?.friendlyMessage || "Errore");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="change-email-dialog">
        <DialogHeader>
          <DialogTitle>Cambia indirizzo email</DialogTitle>
          <DialogDescription>
            La nuova email verrà usata per l'accesso.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Nuova email</Label>
            <Input
              type="email"
              value={form.new_email}
              onChange={(e) => setForm({ ...form, new_email: e.target.value })}
              data-testid="ce-new-email"
            />
          </div>
          <div>
            <Label>Password attuale</Label>
            <Input
              type="password"
              value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })}
              data-testid="ce-password"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={submit} disabled={loading} data-testid="ce-save">
            {loading ? "Salvataggio…" : "Aggiorna email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------- 2FA Setup -------------------- */
export function TwoFactorSetupDialog({ open, onOpenChange, onDone }) {
  const [step, setStep] = useState(1); // 1 = QR, 2 = verify, 3 = backup codes
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1);
      setCode("");
      setBackupCodes([]);
      api
        .post("/auth/2fa/setup")
        .then(({ data }) => setSetup(data))
        .catch((err) => {
          toast.error(err?.friendlyMessage || "Errore");
          onOpenChange(false);
        });
    }
    // eslint-disable-next-line
  }, [open]);

  const verify = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/setup/verify", { code });
      setBackupCodes(data.backup_codes || []);
      setStep(3);
      toast.success("Autenticazione a due fattori attivata");
    } catch (err) {
      toast.error(err?.friendlyMessage || "Codice non valido");
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    if (!setup?.secret) return;
    navigator.clipboard.writeText(setup.secret);
    toast.success("Chiave copiata");
  };

  const copyCodes = () => {
    navigator.clipboard.writeText(backupCodes.join("\n"));
    toast.success("Codici copiati");
  };

  const downloadCodes = () => {
    const blob = new Blob(
      [`TECSend - Codici di riserva 2FA\n\nUtilizza ciascun codice una sola volta.\n\n${backupCodes.join("\n")}\n`],
      { type: "text/plain" }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "tecsend-codici-riserva.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="tfa-setup-dialog">
        <DialogHeader>
          <DialogTitle>Attiva autenticazione a due fattori</DialogTitle>
          <DialogDescription>
            Aggiungi un livello di sicurezza usando Google Authenticator, Microsoft Authenticator o Authy.
          </DialogDescription>
        </DialogHeader>

        {step === 1 && setup && (
          <div className="space-y-4">
            <ol className="text-sm text-muted-foreground list-decimal pl-5 space-y-1">
              <li>Apri l'app Authenticator sul tuo smartphone</li>
              <li>Aggiungi un nuovo account e scansiona il QR qui sotto</li>
              <li>Inserisci il codice a 6 cifre nel prossimo passaggio</li>
            </ol>
            <div className="flex justify-center bg-white border border-border p-4" data-testid="tfa-qr-wrap">
              <img src={setup.qr_svg_data_url} alt="QR 2FA" className="w-56 h-56" />
            </div>
            <details className="text-sm">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                Non riesci a scansionare? Inserisci manualmente
              </summary>
              <div className="mt-3 p-3 border border-border bg-muted/40 rounded-sm flex items-center gap-2">
                <code className="flex-1 text-xs break-all" data-testid="tfa-secret">{setup.secret}</code>
                <Button size="icon" variant="ghost" onClick={copySecret}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </details>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
              <Button onClick={() => setStep(2)} data-testid="tfa-scanned-btn">
                Ho scansionato il QR
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Inserisci il codice a 6 cifre generato dall'app.
            </p>
            <Input
              autoFocus
              inputMode="numeric"
              maxLength={6}
              placeholder="000 000"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="text-center tracking-[0.4em] text-2xl font-mono"
              data-testid="tfa-code-input"
            />
            <div className="flex justify-between gap-2">
              <Button variant="ghost" onClick={() => setStep(1)}>← Indietro</Button>
              <Button onClick={verify} disabled={loading || code.length < 6} data-testid="tfa-verify-btn">
                {loading ? "Verifica…" : "Verifica e attiva"}
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <div className="border border-primary/30 bg-primary/5 p-4 flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-primary shrink-0" strokeWidth={1.5} />
              <div className="text-sm">
                <div className="font-medium">2FA attivata correttamente</div>
                <div className="text-muted-foreground">
                  Salva questi codici di riserva in un posto sicuro. Servono se perdi accesso all'app Authenticator.
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 p-4 border border-border bg-muted/40 font-mono text-sm">
              {backupCodes.map((c) => (
                <div key={c} className="text-center py-1" data-testid={`backup-code-${c}`}>{c}</div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={copyCodes} className="flex-1" data-testid="tfa-copy-codes">
                <Copy className="w-4 h-4 mr-2" /> Copia codici
              </Button>
              <Button variant="outline" onClick={downloadCodes} className="flex-1" data-testid="tfa-download-codes">
                <Download className="w-4 h-4 mr-2" /> Scarica .txt
              </Button>
            </div>
            <div className="border border-destructive/30 bg-destructive/5 p-3 flex items-start gap-2 text-xs">
              <AlertTriangle className="w-4 h-4 text-destructive shrink-0" strokeWidth={1.5} />
              <span>
                Ogni codice è utilizzabile <strong>una sola volta</strong>. Non li mostreremo più.
              </span>
            </div>
            <Button
              className="w-full"
              onClick={() => {
                onOpenChange(false);
                onDone?.();
              }}
              data-testid="tfa-done-btn"
            >
              Fatto, ho salvato i codici
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* -------------------- 2FA Disable -------------------- */
export function TwoFactorDisableDialog({ open, onOpenChange, onDone }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/auth/2fa/disable", { current_password: password });
      toast.success("2FA disattivata");
      onOpenChange(false);
      setPassword("");
      onDone?.();
    } catch (err) {
      toast.error(err?.friendlyMessage || "Errore");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tfa-disable-dialog">
        <DialogHeader>
          <DialogTitle>Disattivare l'autenticazione a due fattori?</DialogTitle>
          <DialogDescription>
            Il tuo account tornerà a proteggersi con la sola password. Non consigliato.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="border border-destructive/30 bg-destructive/5 p-3 flex gap-2 text-sm">
            <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" strokeWidth={1.5} />
            <span>Conferma con la tua password attuale.</span>
          </div>
          <div>
            <Label>Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="tfa-disable-password"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button variant="destructive" onClick={submit} disabled={loading} data-testid="tfa-disable-confirm">
            {loading ? "Disattivazione…" : "Disattiva 2FA"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------- Main SecuritySection component -------------------- */
export default function SecuritySection() {
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [openPwd, setOpenPwd] = useState(false);
  const [openEmail, setOpenEmail] = useState(false);
  const [openTfaSetup, setOpenTfaSetup] = useState(false);
  const [openTfaDisable, setOpenTfaDisable] = useState(false);

  const load = () => api.get("/auth/2fa/status").then(({ data }) => setStatus(data));
  useEffect(() => {
    load();
  }, [user?.two_factor_enabled]);

  const enabled = status?.enabled;

  return (
    <div className="border border-border bg-white p-6" data-testid="security-section">
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck className="w-5 h-5 text-primary" strokeWidth={1.5} />
        <h2 className="font-heading text-xl">Sicurezza account</h2>
      </div>

      <div className="divide-y divide-border -mx-6">
        {/* Email */}
        <div className="px-6 py-4 flex items-center gap-4">
          <Mail className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">Indirizzo email</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          </div>
          <Button variant="outline" onClick={() => setOpenEmail(true)} data-testid="btn-change-email">
            Cambia email
          </Button>
        </div>

        {/* Password */}
        <div className="px-6 py-4 flex items-center gap-4">
          <KeyRound className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">Password</div>
            <div className="text-xs text-muted-foreground">Nascosta per motivi di sicurezza</div>
          </div>
          <Button variant="outline" onClick={() => setOpenPwd(true)} data-testid="btn-change-password">
            Cambia password
          </Button>
        </div>

        {/* 2FA */}
        <div className="px-6 py-4 flex items-center gap-4">
          <Smartphone className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">
              Autenticazione a due fattori
              {enabled && (
                <span className="ml-2 inline-block text-[10px] px-2 py-0.5 uppercase tracking-widest bg-primary text-primary-foreground">
                  Attiva
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              {enabled
                ? `Attiva dal ${new Date(status.enabled_at).toLocaleDateString("it-IT")} · ${status.backup_codes_left} codici di riserva rimanenti`
                : "Aggiungi un codice a 6 cifre da Google Authenticator ad ogni accesso"}
            </div>
          </div>
          {enabled ? (
            <Button variant="outline" onClick={() => setOpenTfaDisable(true)} data-testid="btn-disable-2fa">
              Disattiva
            </Button>
          ) : (
            <Button onClick={() => setOpenTfaSetup(true)} data-testid="btn-enable-2fa">
              Attiva
            </Button>
          )}
        </div>
      </div>

      <ChangePasswordDialog open={openPwd} onOpenChange={setOpenPwd} />
      <ChangeEmailDialog open={openEmail} onOpenChange={setOpenEmail} onDone={refresh} />
      <TwoFactorSetupDialog
        open={openTfaSetup}
        onOpenChange={setOpenTfaSetup}
        onDone={() => {
          refresh();
          load();
        }}
      />
      <TwoFactorDisableDialog
        open={openTfaDisable}
        onOpenChange={setOpenTfaDisable}
        onDone={() => {
          refresh();
          load();
        }}
      />
    </div>
  );
}
